import { StatusTag } from "@/views/components/StatusTag";
import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Input, Select, Space, Statistic } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { AxiosResponse } from "axios";
import type { FenceReminderRecordsVo } from "@zhihaoscm/entity";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
    Dialog as ZhDialog,
    DateRangePicker as ZhDateRangePicker,
    InputNumber as ZhInputNumber,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";
import request from "@/api";
import type { CustomAxiosRequestConfig } from "@/api";
import { DEFAULT_VALUE, LOG_DATE_FORMAT_T } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat } from "@/utils";
import { ACTUAL_STATUS, MONITOR_BEHAVIOR } from "@zhihaoscm/meta/src/fenceReminderRecords";
import { SHIP_STATE } from "@zhihaoscm/meta";

type FenceRow = Partial<FenceReminderRecordsVo> & {
    fenceReminderRecords: NonNullable<Partial<FenceReminderRecordsVo>["fenceReminderRecords"]>;
};
export type FenceRecordQuery = {
    page: number;
    size: number;
    processStatus?: number;
    keywords?: string;
    shipKeywords?: string;
    shipBehavior?: number;
    minTonCapacity?: number;
    maxTonCapacity?: number;
    warehouseStateList?: number[];
    actualStatusList?: number[];
    isInTransit?: number;
    startTime?: string;
    endTime?: string;
    processorKeywords?: string;
    sortKey?: string;
    sortOrder?: string;
};
export type FenceProcessData = {
    processingStatus?: number;
    actualShipStatus?: number;
    location?: string;
    processingDescription: string;
};

/** 复用 Vue 电子围栏记录导出的文件名和下载参数。 */
const ELECTRONIC_FENCE_EXPORT_FILENAME = "电子围栏记录表.xlsx";

/** 将 Vue 导出的 Blob 转成浏览器下载，并确保临时资源始终释放。 */
function downloadElectronicFenceBlob(blob: Blob, filename: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);

    try {
        link.click();
    } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }
}

/** 复用 Vue `/fence-reminder-records/export` 接口，保持当前筛选参数不变。 */
export async function exportElectronicFenceRecords(query: FenceRecordQuery): Promise<void> {
    const response = await request.service.get<Blob, AxiosResponse<Blob>>("/fence-reminder-records/export", {
        params: query,
        responseType: "blob",
        returnRawResponse: true
    } as CustomAxiosRequestConfig);
    downloadElectronicFenceBlob(response.data, ELECTRONIC_FENCE_EXPORT_FILENAME);
}

export async function loadFenceRecords(query: FenceRecordQuery) {
    const { fenceReminderRecordService } = await import("@/api/services/fenceReminderRecordService");
    const result = await fenceReminderRecordService.pagingRecords(query as never);
    // Vue service 的 VO 泛型比 React 页面展示结构更严格，这里只做页面层的字段适配。
    return { list: result.list as unknown as FenceRow[], total: result.total };
}
export async function loadFenceSummary() {
    const { fenceReminderRecordService } = await import("@/api/services/fenceReminderRecordService");
    return fenceReminderRecordService.sum();
}
export async function processFenceRecord(id: string, data: FenceProcessData) {
    const { fenceReminderRecordService } = await import("@/api/services/fenceReminderRecordService");
    return fenceReminderRecordService.handle(id, {
        processStatus: data.processingStatus || 3,
        actualStatus: Number(data.actualShipStatus || 0),
        location: data.location || "",
        remark: data.processingDescription
    });
}

/** 复用 Vue 的批量指派契约：ID 列表放在请求体，处理人 ID 放在查询参数。 */
export async function assignBatchFenceRecords(processorId: number, ids: number[]) {
    const { fenceReminderRecordService } = await import("@/api/services/fenceReminderRecordService");
    return fenceReminderRecordService.assignBatch(processorId, ids);
}

const statusLabel = (state?: number) => ({ 1: "未处理", 2: "持续跟进", 3: "已处理完成" })[Number(state)] || "未知";

export interface ElectronicFenceRecordPageProps {
    loadRecords?: (query: FenceRecordQuery) => Promise<{ list?: FenceRow[]; total?: number }>;
    loadSum?: () => Promise<{
        totalCount?: number;
        unProcessedCount?: number;
        followingUpCount?: number;
        processedCount?: number;
    }>;
    handleRecord?: (id: string, data: FenceProcessData) => Promise<unknown>;
    assignBatch?: (processorId: number, ids: number[]) => Promise<unknown>;
    exportRecords?: (query: FenceRecordQuery) => Promise<void>;
    permissions?: string[];
}

/** 电子围栏记录：保留 Vue 的状态卡片、处理入口和显式请求触发。 */
export function ElectronicFenceRecordPage({
    loadRecords = loadFenceRecords,
    loadSum = loadFenceSummary,
    handleRecord = processFenceRecord,
    assignBatch = assignBatchFenceRecords,
    exportRecords = exportElectronicFenceRecords,
    permissions
}: ElectronicFenceRecordPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const allPermissions = permissions || storePermissions;
    const canWrite = allPermissions.includes("ROLE_ELECTRIC_FENCE_W");
    const canDeal = allPermissions.includes("ROLE_ELECTRIC_FENCE_DEAL");
    const canExport = allPermissions.includes("ROLE_ELECTRIC_FENCE_EXPORT");
    const [rows, setRows] = useState<FenceRow[]>([]);
    const [total, setTotal] = useState(0);
    // 保留 Vue ProTable 的当前页状态，筛选条件变化时由页面逻辑显式复位。
    const [page, setPage] = useState(1);
    // 旧 Vue 列表默认每页 10 条，筛选和翻页请求统一从该状态取值。
    const [pageSize, setPageSize] = useState(10);
    const [status, setStatus] = useState<number | undefined>();
    const [keyword, setKeyword] = useState("");
    const [queryKeyword, setQueryKeyword] = useState("");
    const [shipKeywords, setShipKeywords] = useState("");
    const [shipBehavior, setShipBehavior] = useState<number | undefined>();
    const [minTonCapacity, setMinTonCapacity] = useState<number | undefined>();
    const [maxTonCapacity, setMaxTonCapacity] = useState<number | undefined>();
    const [warehouseStateList, setWarehouseStateList] = useState<number[]>([]);
    const [actualStatusList, setActualStatusList] = useState<number[]>([]);
    const [isInTransit, setIsInTransit] = useState<number | undefined>();
    const [triggerTime, setTriggerTime] = useState<[string, string] | null>(null);
    const [processorKeywords, setProcessorKeywords] = useState("");
    const [sortKey, setSortKey] = useState("");
    const [sortOrder, setSortOrder] = useState("");
    const [selected, setSelected] = useState<string[]>([]);
    const [summary, setSummary] = useState<Record<string, number>>({});
    const [error, setError] = useState("");
    const [processing, setProcessing] = useState<FenceRow | null>(null);
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [processorId, setProcessorId] = useState<number | null>(null);
    const [assigning, setAssigning] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [refreshToken, setRefreshToken] = useState(0);

    /** 将 Vue columns 的筛选模型转换为后端原有字段，空值不提交。 */
    const getEffectiveQuery = (currentPage: number, currentPageSize = pageSize): FenceRecordQuery => ({
        page: currentPage,
        size: currentPageSize,
        ...(status === undefined ? {} : { processStatus: status }),
        ...(queryKeyword ? { keywords: queryKeyword } : {}),
        ...(shipKeywords.trim() ? { shipKeywords: shipKeywords.trim() } : {}),
        ...(shipBehavior === undefined ? {} : { shipBehavior }),
        ...(minTonCapacity === undefined ? {} : { minTonCapacity }),
        ...(maxTonCapacity === undefined ? {} : { maxTonCapacity }),
        ...(warehouseStateList.length ? { warehouseStateList } : {}),
        ...(actualStatusList.length ? { actualStatusList } : {}),
        ...(isInTransit === undefined ? {} : { isInTransit }),
        // 旧 Vue 页面提交 ISO 时间戳，保持后端对触发时间筛选的解析契约。
        ...(triggerTime?.[0] ? { startTime: dateFormat(triggerTime[0], LOG_DATE_FORMAT_T) } : {}),
        ...(triggerTime?.[1] ? { endTime: dateFormat(triggerTime[1], LOG_DATE_FORMAT_T) } : {}),
        ...(processorKeywords.trim() ? { processorKeywords: processorKeywords.trim() } : {}),
        ...(sortKey ? { sortKey, sortOrder } : {})
    });

    useEffect(() => {
        void loadSum()
            .then(value => setSummary(value as Record<string, number>))
            .catch(() => undefined);
    }, [loadSum, refreshToken]);
    useEffect(() => {
        let active = true;
        void loadRecords(getEffectiveQuery(page))
            .then(result => {
                if (active) {
                    setRows(result.list || []);
                    setTotal(result.total || 0);
                    setError("");
                }
            })
            .catch(() => {
                if (active) {
                    setRows([]);
                    setTotal(0);
                    setError("电子围栏记录加载失败");
                }
            });
        return () => {
            active = false;
        };
    }, [
        loadRecords,
        page,
        pageSize,
        queryKeyword,
        status,
        shipKeywords,
        shipBehavior,
        minTonCapacity,
        maxTonCapacity,
        warehouseStateList,
        actualStatusList,
        isInTransit,
        triggerTime,
        processorKeywords,
        sortKey,
        sortOrder,
        refreshToken
    ]);
    const submit = () => {
        setPage(1);
        setQueryKeyword(keyword.trim());
        setPage(1);
    };
    const process = async () => {
        if (!processing || saving) return;
        setSaving(true);
        try {
            await handleRecord(String(processing.fenceReminderRecords.id), {
                processingStatus: 3,
                actualShipStatus: processing.fenceReminderRecords.actualStatus,
                location: processing.fenceReminderRecords.location,
                processingDescription: description
            });
            setProcessing(null);
            setDescription("");
            setRefreshToken(value => value + 1);
        } catch {
            setError("电子围栏处理失败");
        } finally {
            setSaving(false);
        }
    };
    const openBatchAssign = () => {
        // 二次校验选中状态，避免分页刷新或旧状态导致已处理记录被提交。
        if (selected.length === 0) return;
        const selectedRows = rows.filter(row => selected.includes(String(row.fenceReminderRecords.id)));
        if (selectedRows.some(row => Number(row.fenceReminderRecords.processStatus) === 3)) {
            setError("已处理完成的记录不能批量指派");
            return;
        }
        setError("");
        setProcessorId(null);
        setAssignModalOpen(true);
    };
    const submitBatchAssign = async () => {
        // Vue 页面要求处理人 ID 必填，且只提交合法的数字 ID。
        if (!processorId || processorId <= 0 || assigning || selected.length === 0) {
            if (!processorId || processorId <= 0) setError("请选择处理人");
            return;
        }
        const ids = selected.map(Number).filter(id => Number.isFinite(id) && id > 0);
        if (ids.length === 0) {
            setError("请选择要指派的记录");
            return;
        }
        setAssigning(true);
        try {
            await assignBatch(processorId, ids);
            setAssignModalOpen(false);
            setSelected([]);
            setError("");
            // 刷新列表和统计卡片，保证成功后的处理人和数量与服务端一致。
            setRefreshToken(value => value + 1);
        } catch {
            setError("批量指派失败");
        } finally {
            setAssigning(false);
        }
    };
    const submitExport = async () => {
        // 只提交页面当前已生效的筛选条件，不把输入框中尚未查询的值带入 Vue 导出接口。
        const query = getEffectiveQuery(1);
        setExporting(true);
        setError("");
        try {
            await exportRecords(query);
        } catch {
            // 导出失败时保留当前列表和筛选状态，并给出页面级反馈。
            setError("电子围栏记录导出失败");
        } finally {
            setExporting(false);
        }
    };

    // 用 zhihao-ui-react 的列契约承接旧 Vue 电子围栏 ZhTable 的字段和操作入口。
    const fenceColumns = useMemo<TableColumnProps<FenceRow>[]>(
        () => [
            ...(canWrite ? [{ type: "selection" as const, width: 55, fixed: "left" as const }] : []),
            {
                key: "code",
                label: "编号",
                width: 140,
                prop: "fenceReminderRecords",
                render: ({ row }) => (
                    <Link to={`/logistics/cluesManage/electronicFenceRecord/${row.fenceReminderRecords.id}`}>
                        {row.fenceReminderRecords.code}
                    </Link>
                )
            },
            {
                key: "reminderName",
                label: "提醒名称",
                width: 150,
                prop: "fenceReminderRecords",
                render: ({ row }) => row.fenceReminderRecords.reminderName || DEFAULT_VALUE
            },
            {
                key: "markName",
                label: "区域名称",
                width: 150,
                prop: "fenceReminderRecords",
                render: ({ row }) => row.fenceReminderRecords.markName || DEFAULT_VALUE
            },
            {
                key: "shipBehavior",
                label: "船舶行为",
                width: 120,
                prop: "fenceReminderRecords",
                render: ({ row }) =>
                    MONITOR_BEHAVIOR.getIntroByCode(row.fenceReminderRecords.shipBehavior) || DEFAULT_VALUE
            },
            {
                key: "mmsi",
                label: "MMSI",
                width: 120,
                prop: "ship",
                render: ({ row }) => row.ship?.id || row.fenceReminderRecords.mmsi || DEFAULT_VALUE
            },
            {
                key: "shipName",
                label: "船名",
                width: 150,
                prop: "shipName",
                render: ({ row }) =>
                    row.fenceReminderRecords.shipName || row.ship?.name || row.ship?.cnname || DEFAULT_VALUE
            },
            {
                key: "tonCapacity",
                label: "载重吨",
                width: 120,
                prop: "fenceReminderRecords",
                sortable: "custom",
                render: ({ row }) =>
                    String(row.fenceReminderRecords.tonCapacity || row.ship?.tonCapacity || DEFAULT_VALUE)
            },
            {
                key: "shipState",
                label: "认证",
                width: 120,
                prop: "ship",
                render: ({ row }) => SHIP_STATE.getIntroByCode(row.ship?.state) || DEFAULT_VALUE
            },
            {
                key: "previousLocation",
                label: "前一天位置",
                width: 200,
                prop: "fenceReminderRecords",
                render: ({ row }) => row.fenceReminderRecords.previousLocation || DEFAULT_VALUE
            },
            {
                key: "warehouseState",
                label: "货仓状态",
                width: 120,
                prop: "ship",
                render: ({ row }) => row.ship?.warehouseState ?? DEFAULT_VALUE
            },
            {
                key: "actualStatus",
                label: "船舶实际状态",
                width: 180,
                prop: "fenceReminderRecords",
                render: ({ row }) =>
                    ACTUAL_STATUS.getIntroByCode(row.fenceReminderRecords.actualStatus) || DEFAULT_VALUE
            },
            {
                key: "location",
                label: "所在地",
                width: 120,
                prop: "fenceReminderRecords",
                render: ({ row }) => row.fenceReminderRecords.location || DEFAULT_VALUE
            },
            {
                key: "status",
                label: "处理状态",
                width: 120,
                prop: "fenceReminderRecords",
                render: ({ row }) => <StatusTag>{statusLabel(row.fenceReminderRecords.processStatus)}</StatusTag>
            },
            {
                key: "isInTransit",
                label: "是否在途",
                width: 120,
                prop: "isInTransit",
                render: ({ row }) => (row.isInTransit == null ? DEFAULT_VALUE : row.isInTransit ? "是" : "否")
            },
            {
                key: "transportOrderShipId",
                label: "运单编号",
                width: 150,
                prop: "transportOrderShipId",
                render: ({ row }) => row.transportOrderShipId || DEFAULT_VALUE
            },
            {
                key: "projectName",
                label: "当前项目",
                width: 180,
                prop: "projectName",
                render: ({ row }) => row.projectName || DEFAULT_VALUE
            },
            {
                key: "triggerTime",
                label: "触发时间",
                width: 180,
                prop: "fenceReminderRecords",
                render: ({ row }) =>
                    String(row.fenceReminderRecords.triggerTime || DEFAULT_VALUE)
                        .replace("T", " ")
                        .slice(0, 19)
            },
            {
                key: "processorKeywords",
                label: "处理人",
                width: 120,
                prop: "processorUser",
                render: ({ row }) => row.processorUser?.name || DEFAULT_VALUE
            },
            {
                key: "operation",
                label: "操作",
                width: 200,
                prop: "operation",
                fixed: "right",
                render: ({ row }) => (
                    <Space>
                        {canWrite && [1, 2].includes(Number(row.fenceReminderRecords.processStatus)) ? (
                            <Button disabled={banButton} type="link">
                                {row.processorUser?.id ? "变更指派" : "指派"}
                            </Button>
                        ) : null}
                        {canDeal && [1, 2].includes(Number(row.fenceReminderRecords.processStatus)) ? (
                            <Button
                                disabled={banButton}
                                type="link"
                                onClick={() => {
                                    setProcessing(row);
                                    setDescription("");
                                }}
                            >
                                处理
                            </Button>
                        ) : null}
                    </Space>
                )
            }
        ],
        [banButton, canDeal, canWrite]
    );

    return (
        <section aria-label="electronic-fence-record-page">
            <Card title="电子围栏记录">
                <Space wrap>
                    <Statistic title="全部记录" value={summary.totalCount || 0} />
                    <Statistic title="未处理" value={summary.unProcessedCount || 0} />
                    <Statistic title="持续跟进" value={summary.followingUpCount || 0} />
                    <Statistic title="已处理完成" value={summary.processedCount || 0} />
                </Space>
            </Card>
            <Card>
                <Space wrap>
                    <Input
                        aria-label="围栏关键词"
                        placeholder="编号/提醒名称/区域名称"
                        value={keyword}
                        onChange={event => setKeyword(event.target.value)}
                    />
                    <Input
                        aria-label="提醒名称"
                        placeholder="请输入编号/提醒名称/区域名称搜索"
                        value={keyword}
                        onChange={event => setKeyword(event.target.value)}
                    />
                    <Input
                        aria-label="MMSI"
                        placeholder="请输入MMSI/船名搜索"
                        value={shipKeywords}
                        onChange={event => setShipKeywords(event.target.value)}
                    />
                    <Select
                        aria-label="船舶行为"
                        allowClear
                        placeholder="请选择船舶行为"
                        value={shipBehavior}
                        options={MONITOR_BEHAVIOR.options.map(item => ({ value: item.value, label: item.label }))}
                        onChange={setShipBehavior}
                    />
                    <ZhInputNumber
                        aria-label="最小载重吨"
                        min={0}
                        placeholder="最小载重吨"
                        value={minTonCapacity ?? ""}
                        onChange={value => setMinTonCapacity(value === "" ? undefined : Number(value))}
                    />
                    <ZhInputNumber
                        aria-label="最大载重吨"
                        min={0}
                        placeholder="最大载重吨"
                        value={maxTonCapacity ?? ""}
                        onChange={value => setMaxTonCapacity(value === "" ? undefined : Number(value))}
                    />
                    <ZhDateRangePicker
                        aria-label="触发时间"
                        rangeSeparator="-"
                        type="datetimerange"
                        startPlaceholder="触发开始时间"
                        endPlaceholder="触发结束时间"
                        valueFormat="YYYY-MM-DD HH:mm:ss"
                        value={triggerTime}
                        onChange={(_, formattedValue) => setTriggerTime(formattedValue)}
                    />
                    <Input
                        aria-label="处理人"
                        placeholder="请输入处理人名称搜索"
                        value={processorKeywords}
                        onChange={event => setProcessorKeywords(event.target.value)}
                    />
                    <Button type="primary" onClick={submit}>
                        查询
                    </Button>
                    <Button
                        onClick={() => {
                            setKeyword("");
                            setPage(1);
                            setQueryKeyword("");
                        }}
                    >
                        重置
                    </Button>
                    <Select
                        aria-label="处理状态"
                        allowClear
                        placeholder="处理状态"
                        value={status}
                        onChange={value => {
                            setPage(1);
                            setStatus(value);
                        }}
                        options={[
                            { value: 1, label: "未处理" },
                            { value: 2, label: "持续跟进" },
                            { value: 3, label: "已处理完成" }
                        ]}
                    />
                    <Select
                        aria-label="是否在途"
                        allowClear
                        placeholder="请选择是否在途"
                        value={isInTransit}
                        options={[
                            { value: 1, label: "是" },
                            { value: 0, label: "否" }
                        ]}
                        onChange={setIsInTransit}
                    />
                    <Select
                        aria-label="船舶实际状态"
                        maxTagCount={1}
                        mode="multiple"
                        placeholder="请选择船舶实际状态"
                        value={actualStatusList}
                        options={ACTUAL_STATUS.options.map(item => ({ value: Number(item.value), label: item.label }))}
                        onChange={setActualStatusList}
                    />
                    <Select
                        aria-label="货仓状态"
                        maxTagCount={1}
                        mode="multiple"
                        placeholder="请选择货仓状态"
                        value={warehouseStateList}
                        options={[
                            { value: 1, label: "正常" },
                            { value: 2, label: "异常" }
                        ]}
                        onChange={setWarehouseStateList}
                    />
                    <Button
                        onClick={() => {
                            setSortKey("");
                            setSortOrder("");
                        }}
                    >
                        清除排序
                    </Button>
                    {canExport ? (
                        <Button
                            loading={exporting}
                            disabled={banButton || exporting}
                            onClick={() => void submitExport()}
                        >
                            导出
                        </Button>
                    ) : null}
                    {canWrite ? (
                        <>
                            <Button
                                disabled={banButton || selected.length === 0 || assigning}
                                onClick={openBatchAssign}
                            >
                                批量指派
                            </Button>
                            <Button disabled={banButton || selected.length === 0}>轮询分配</Button>
                        </>
                    ) : null}
                </Space>
            </Card>
            {error ? <Alert message={error} type="error" showIcon /> : null}
            <Card>
                <ZhTable<FenceRow>
                    rowKey={row => String(row.fenceReminderRecords.id)}
                    data={rows}
                    columns={normalizeOperationColumns(fenceColumns, rows)}
                    pagination={false}
                    onSortChange={({ prop, order }) => {
                        setSortKey(prop === "fenceReminderRecords" ? "ton_capacity" : prop || "");
                        setSortOrder(order === "ascending" ? "asc" : order === "descending" ? "desc" : "");
                        setPage(1);
                    }}
                    onSelectionChange={selection =>
                        setSelected(selection.map(row => String(row.fenceReminderRecords.id)))
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={page}
                                pageSize={pageSize}
                                total={total}
                                onChange={(nextPage, nextPageSize) => {
                                    setPageSize(nextPageSize);
                                    setPage(nextPageSize !== pageSize ? 1 : nextPage);
                                }}
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                open={Boolean(processing)}
                title="处理"
                width={400}
                onCancel={() => {
                    if (!saving) setProcessing(null);
                }}
                footer={
                    <Space>
                        <Button disabled={saving} onClick={() => setProcessing(null)}>
                            取消
                        </Button>
                        <Button loading={saving} type="primary" onClick={() => void process()}>
                            确认处理
                        </Button>
                    </Space>
                }
            >
                <Input.TextArea
                    aria-label="处理说明"
                    rows={4}
                    value={description}
                    onChange={event => setDescription(event.target.value)}
                />
            </ZhDialog>
            <ZhDialog
                open={assignModalOpen}
                title="批量指派"
                width={400}
                onCancel={() => {
                    if (!assigning) setAssignModalOpen(false);
                }}
                footer={
                    <Space>
                        <Button disabled={assigning} onClick={() => setAssignModalOpen(false)}>
                            取消
                        </Button>
                        <Button loading={assigning} type="primary" onClick={() => void submitBatchAssign()}>
                            确认指派
                        </Button>
                    </Space>
                }
            >
                <ZhInputNumber
                    aria-label="处理人ID"
                    min={1}
                    precision={0}
                    placeholder="请输入处理人ID"
                    value={processorId ?? ""}
                    onChange={value => setProcessorId(value === "" ? null : Number(value))}
                    style={{ width: "100%" }}
                />
            </ZhDialog>
        </section>
    );
}

export default ElectronicFenceRecordPage;
